// Mirrors ApproachDeployWizardOrganism.renderInputByType (design-system) so the
// admin Add-Organization flow collects deploymentInputs with the same per-type
// affordances. Controlled component: parent owns the values map.
import React from 'react';
import { useTranslation } from 'localization';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputAtom } from 'design-system/my-lib';
import { ApproachDeploymentInput } from 'types/approach';
import type { LocalizedString } from 'types/context';
import './DeploymentInputsFormMolecule.scss';

function resolveLabel(label: LocalizedString | string | undefined, fallback: string): string {
  if (!label) return fallback;
  if (typeof label === 'string') return label;
  return label.default || fallback;
}

export interface DeploymentInputsFormMoleculeProps {
  inputs: ApproachDeploymentInput[];
  values: Record<string, unknown>;
  onChange: (inputId: string, value: unknown) => void;
}

const DeploymentInputsFormMolecule: React.FC<DeploymentInputsFormMoleculeProps> = ({
  inputs,
  values,
  onChange,
}) => {
  const { t } = useTranslation();

  const renderInput = (input: ApproachDeploymentInput) => {
    const val = values[input.id];
    const fieldId = `deployment-input-${input.id}`;
    const fieldLabel = resolveLabel(input.label, input.id);

    switch (input.inputType) {
      case 'number':
        return (
          <InputNumber
            inputId={fieldId}
            data-testid={fieldId}
            value={(val as number) ?? null}
            onValueChange={(e) => onChange(input.id, e.value)}
          />
        );
      case 'date':
        return (
          <Calendar
            inputId={fieldId}
            data-testid={fieldId}
            value={(val as Date) ?? null}
            onChange={(e) => onChange(input.id, e.value)}
            dateFormat="yy-mm-dd"
            showIcon
          />
        );
      case 'date_range':
        return (
          <Calendar
            inputId={fieldId}
            data-testid={fieldId}
            value={(val as Date[] | null) ?? null}
            onChange={(e) => onChange(input.id, e.value)}
            dateFormat="yy-mm-dd"
            selectionMode="range"
            showIcon
          />
        );
      case 'select':
        return (
          <Dropdown
            className="deployment-inputs-form__select"
            inputId={fieldId}
            data-testid={fieldId}
            value={(val as string) ?? null}
            options={(input.options || []).map((opt) => ({
              label: resolveLabel(opt.label, opt.value),
              value: opt.value,
            }))}
            onChange={(e) => onChange(input.id, e.value)}
            pt={{ trigger: { 'aria-label': fieldLabel } }}
          />
        );
      case 'boolean':
        return (
          <Checkbox
            inputId={fieldId}
            data-testid={fieldId}
            checked={!!val}
            onChange={(e) => onChange(input.id, e.checked)}
          />
        );
      // person_select / sphere_select have no picker atom yet — fall back to a
      // free-text id field (same behavior as the marketplace deploy wizard).
      case 'text':
      case 'person_select':
      case 'sphere_select':
      default:
        return (
          <InputAtom
            id={fieldId}
            data-testid={fieldId}
            value={(val as string) ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(input.id, e.target.value)
            }
          />
        );
    }
  };

  return (
    <div className="deployment-inputs-form">
      {inputs.map((input) => (
        <div key={input.id} className="deployment-inputs-form__field">
          <label className="deployment-inputs-form__label" htmlFor={`deployment-input-${input.id}`}>
            {resolveLabel(input.label, input.id)}
            {input.required && (
              <span className="deployment-inputs-form__required" aria-hidden="true">
                {' *'}
              </span>
            )}
          </label>
          {input.description && (
            <span className="deployment-inputs-form__description">
              {resolveLabel(input.description, '')}
            </span>
          )}
          {renderInput(input)}
        </div>
      ))}
      {inputs.length === 0 && (
        <p className="deployment-inputs-form__empty">
          {t('admin.organizations.addDialog.deploymentInputs.none')}
        </p>
      )}
    </div>
  );
};

export default DeploymentInputsFormMolecule;
